import { useState } from "react";
import { decodeRef, getAccountTransactions, getTransactions, makeJournalEntry } from "./accounts_halper";
import { Box, Button, FormControlLabel, FormGroup, LinearProgress, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import HtmlTooltip from "../frames/html_tooltip";
import { useParams } from "react-router-dom";
import { getOfficeMeta } from "../frames/common";

function PrintTransactionList() {
    const [transactions, setTransactions] = useState(null);
    const [allTransactions, setAllTransactions] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFirst, setIsFirst] = useState(true);
    const [loadMore, setLoadMore] = useState(true);
    const [page, setPage] = useState(0);
    const [office, setOffice] = useState(true);
    const [accInfo, setAccInfo] = useState({});
    const [isFull, setIsFull] = useState(true);
    const param = useParams();
    const account = parseInt(param.account ?? "0");

    const initData = async (auth) => {
        if (!auth) return;
        setOffice(await getOfficeMeta());
        if (account === 0) {
            const trans = await getTransactions();
            trans.forEach(itm => {
                itm.balance = 0;
            });
            setTransactions(trans);
            setAllTransactions(trans);
        } else {
            const response = await getAccountTransactions(account, page);
            const trans = response.transaction;
            setAccInfo({
                ac_id: response.ac_id, ac_type: response.ac_type, balance: response.balance,
                created_on: response.created_on, name: response.name,
                opening_balance: response.opening_balance, opening_date: response.opening_date,
                scheme: response.scheme
            });
            const jr = makeJournalEntry(trans, response.opening_balance, response.opening_date, account, false);
            setAllTransactions(jr);
            setTransactions(jr);
        }
    }

    const loadMoreData = async (e) => {
        setIsLoading(true);
        const response = await getAccountTransactions(account, page + 1);
        if (response.transaction.length === 0) {
            setLoadMore(false);
        } else {
            const jr = makeJournalEntry(response.transaction, allTransactions[allTransactions.length - 1].balance, response.opening_date, account, true);
            setTransactions([...allTransactions, ...jr]);
            setAllTransactions([...allTransactions, ...jr]);
        }
        setIsLoading(false);
        setPage(page + 1);
    }

    if (isFirst) {
        setIsFirst(false);
        initData(true).then((res) => {
            setIsLoading(false);
        }).catch((err) => {
            console.error(err);
            setIsLoading(false);
        });
    }

    return (
        <>
            <Box alignSelf={"center"} alignContent={"center"} align="center">
                <Typography variant="h6" align="center">{office.name}</Typography>
                <Typography variant="body2" align="center">{office.add_1}</Typography>
                <Typography variant="body2" align="center">{office.add_2}</Typography>
                <Typography variant="body2" className="invoice-title" sx={{ mt: 1 }} style={{ letterSpacing: "5px" }}>Ledger Account</Typography>
                <Button variant="contained" className="print-btn" style={{ textTransform: "unset" }} onClick={() => { window.print(); }}>
                    &emsp; Print &emsp;
                </Button>
                <FormGroup className="print-btn" sx={{ display: "inline-block", ml: 2 }}>
                    <FormControlLabel control={<Switch defaultChecked style={{ textTransform: "unset" }} onClick={() => { setIsFull(!isFull) }} />} label={isFull ? "Make Compact" : "Detailed Accounts"} />
                </FormGroup>
                {isLoading ? <LinearProgress color="primary" /> : ""}
            </Box>
            <Box sx={{ mt: 1 }}>
                <table cellPadding={0} cellSpacing={0}>
                    <tbody>
                        <HtmlTooltip title={<Typography variant="caption" color="primary">
                            Created: {accInfo.created_on}<br />
                            Opening Date: {accInfo.opening_date}<br />
                            Opening Balance: {accInfo.opening_balance}
                        </Typography>}>
                            <tr>
                                <td>
                                    <Typography variant="subtitle2" color="textPrimary">
                                        Account &emsp;
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="subtitle2" color="textPrimary">
                                        : {accInfo.name}
                                    </Typography>
                                </td>
                            </tr>
                        </HtmlTooltip>
                        <tr>
                            <td>
                                {account === 0 ?
                                    <Typography variant="subtitle2" color="primary">General Ledger</Typography>
                                    :
                                    <Typography variant="subtitle2" color="primary">Balance &emsp;</Typography>
                                }
                            </td>
                            <td>
                                {account === 0 ?
                                    ""
                                    :
                                    <Typography variant="subtitle2" color="primary">: ₹ {accInfo.balance?.toFixed(2) ?? "0.00"}</Typography>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>

                {
                    transactions === null || (transactions[0]?.balance.toFixed(2) ?? "0.00") === (accInfo?.balance.toFixed(2) ?? "0.00")
                        ? "" : <Typography variant="subtitle2" color="error">❌ Critical Audit Error</Typography>
                }
            </Box>
            <Box>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="Transaction List" className="wrap" sx={{ zoom: 0.8 }}>
                        <TableHead>
                            <TableRow sx={{ '&:last-child td, &:last-child th': { fontWeight: "bold" } }}>
                                <TableCell>Date</TableCell>
                                <TableCell>Trn #</TableCell>
                                {/* <TableCell>Debit</TableCell>
                                <TableCell>Credit</TableCell> */}
                                <TableCell>Description</TableCell>
                                <TableCell>Ref</TableCell>
                                <TableCell align="right">Debit</TableCell>
                                <TableCell align="right">Credit</TableCell>
                                {account ? <TableCell align="right">Balance</TableCell> : ""}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions === null || transactions.length === 0 ? <TableRow><TableCell colSpan={6}><Typography textAlign={"center"} color="textDisabled" variant="caption">No Transaction ...</Typography></TableCell></TableRow> :
                                transactions.map((row, idx) =>
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>{row.vhr_date}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <HtmlTooltip title={row.serial_ref === null ? "" : <Typography variant="caption" color="primary">{decodeRef(row.ref)} Serial: {row.serial_ref ?? ""}</Typography>}>
                                                <Typography variant="body2" noWrap>{row.vch_num}</Typography>
                                            </HtmlTooltip>
                                        </TableCell>
                                        {/* <TableCell>
                                            <Typography variant="body2">{row.debit_name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.credit_name}</Typography>
                                        </TableCell> */}
                                        <TableCell>
                                            {
                                                row.description === "Opening balance" ?
                                                    <Typography variant="body2" color="textSecondary">FY {row.description}</Typography> :
                                                    isFull ?
                                                        <Typography variant="body2">
                                                            <Typography variant="caption" color="textPrimary">
                                                                <dl>
                                                                    <dt>{row.debit_name} A/c (Dr) to</dt>
                                                                    <dd>{row.credit_name} A/c (Cr)</dd>
                                                                </dl>
                                                                {row.description} {row.description.startsWith("Invoice issued") ? " - Inv. " + row.serial_ref : ""}
                                                            </Typography>
                                                        </Typography>
                                                        :
                                                        <Typography variant="body2">
                                                            <HtmlTooltip title={<Typography variant="caption" color="primary">
                                                                <dl>
                                                                    <dt>{row.debit_name} A/c (Dr) to</dt>
                                                                    <dd>{row.credit_name} A/c (Cr)</dd>
                                                                </dl>
                                                                ({row.description} {row.description.startsWith("Invoice issued") ? " - Inv. " + row.serial_ref : ""})</Typography>}>
                                                                {row.description} {row.description.startsWith("Invoice issued") ? " - Inv. " + row.serial_ref : ""}
                                                            </HtmlTooltip>
                                                        </Typography>

                                            }
                                        </TableCell>
                                        <TableCell>
                                            <HtmlTooltip title={<Typography variant="caption" color="primary">Type: {row.vhr_type}<br />Refrence: {decodeRef(row.ref)}</Typography>}>
                                                <Typography variant="body2">{row.ref}</Typography>
                                            </HtmlTooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" noWrap>{row.debit_amt.toFixed(2)}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" noWrap>{row.credit_amt.toFixed(2)}</Typography>
                                        </TableCell>
                                        {account ?
                                            <TableCell align="right">
                                                <Typography variant="body2" noWrap>{row.balance.toFixed(2)}</Typography>
                                            </TableCell> : ""}
                                    </TableRow>
                                )
                            }
                            {
                                page === 0 || !accInfo.hasOwnProperty("opening_date") ? "" :
                                    <>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body2"></Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2"></Typography>
                                            </TableCell>

                                            <TableCell colSpan={4}>
                                                <Typography variant="body2" fontWeight={"bold"}>Balance CF</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography variant="body2">{page === 0 ? accInfo.opening_balance : (transactions[transactions.length - 1].balance - transactions[transactions.length - 1].credit_amt + transactions[transactions.length - 1].debit_amt).toFixed(2)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </>
                            }
                        </TableBody>
                    </Table>
                    {loadMore ?
                        <Button variant="text" fullWidth textAlign={"center"} className="print-btn" style={{ textTransform: "unset" }} onClick={loadMoreData}>
                            <Typography variant="caption">Load more ...</Typography>
                        </Button>
                        : ""
                    }
                </TableContainer>
            </Box>
        </>
    );
}


export default PrintTransactionList;
